import type { MovieAwardRow, PersonAwardRow } from './awards'
import { useTmdbLazyMovie } from '../library/useTmdbLazy'
import { LazyImage } from '../ui'

interface Props {
  movies: MovieAwardRow[]
  people: PersonAwardRow[]
  onOpenDetail: (tmdbId: number, type: 'movie' | 'tv') => void
  onPersonOpen: (personId: number) => void
}

/**
 * Desktop parity: two side-by-side leaderboard cards — "Most Awarded
 * Movies" and "Most Awarded People" — with rank, hydrated title / year
 * and a wins / nominations pair on the right.
 *
 * Each row lazy-loads its TMDB poster on scroll so 40 rows don't burn
 * 40 TMDB calls on first mount.
 */
export function LeaderboardSection({
  movies,
  people,
  onOpenDetail,
  onPersonOpen,
}: Props) {
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <LeaderboardCard
        title="Most Awarded Movies"
        iconColor="text-blue-400"
        icon="\uD83C\uDFAC"
        empty="No movies have nominations yet."
      >
        {movies.length === 0 ? null : (
          <ol className="space-y-1.5">
            {movies.map((row, i) => (
              <li key={`m-${row.tmdbId}`}>
                <MovieRow
                  rank={i + 1}
                  row={row}
                  onOpen={() => onOpenDetail(row.tmdbId, 'movie')}
                />
              </li>
            ))}
          </ol>
        )}
      </LeaderboardCard>

      <LeaderboardCard
        title="Most Awarded People"
        iconColor="text-purple-400"
        icon="\uD83C\uDFAD"
        empty="No people have nominations yet."
      >
        {people.length === 0 ? null : (
          <ol className="space-y-1.5">
            {people.map((row, i) => (
              <li key={`p-${row.name}-${i}`}>
                <PersonRow
                  rank={i + 1}
                  row={row}
                  onOpen={
                    row.tmdbId != null ? () => onPersonOpen(row.tmdbId!) : undefined
                  }
                />
              </li>
            ))}
          </ol>
        )}
      </LeaderboardCard>
    </div>
  )
}

function LeaderboardCard({
  title,
  icon,
  iconColor,
  empty,
  children,
}: {
  title: string
  icon: string
  iconColor: string
  empty: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-white/5 bg-bg-secondary/30 p-5 sm:p-6">
      <h3 className="mb-5 flex items-center gap-2.5 text-lg font-bold text-text-primary sm:text-xl">
        <span className={iconColor} aria-hidden>
          {icon}
        </span>
        {title}
      </h3>
      {children ?? (
        <div className="py-10 text-center text-sm text-text-tertiary">
          {empty}
        </div>
      )}
    </div>
  )
}

function MovieRow({
  rank,
  row,
  onOpen,
}: {
  rank: number
  row: MovieAwardRow
  onOpen: () => void
}) {
  const { details: tmdb, ref } = useTmdbLazyMovie(row.tmdbId)
  const title = tmdb?.title ?? row.title
  const year = tmdb?.year
  const poster = tmdb?.posterUrl

  return (
    <button
      ref={ref as (el: HTMLButtonElement | null) => void}
      type="button"
      onClick={onOpen}
      className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5"
    >
      <span className="w-7 shrink-0 text-center text-sm font-black text-text-tertiary transition-colors group-hover:text-accent-primary">
        #{rank}
      </span>
      <div className="h-14 w-10 shrink-0 overflow-hidden rounded border border-border-primary bg-bg-tertiary">
        {poster ? (
          <LazyImage
            src={poster}
            alt={title}
            className="h-full w-full"
            placeholderColor="#1a1a1a"
            fallback={
              <div className="grid h-full w-full place-items-center text-[9px] text-text-tertiary">
                {title.slice(0, 3)}
              </div>
            }
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-[9px] text-text-tertiary">
            {title.slice(0, 3)}
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-text-primary transition-colors group-hover:text-accent-primary">
          {title}
        </p>
        {year && <p className="text-[11px] text-accent-gold">{year}</p>}
      </div>
      <div className="flex shrink-0 gap-3 text-right text-xs">
        <div className="w-10">
          <div className="text-sm font-bold text-accent-gold">{row.wins}</div>
          <div className="text-[10px] uppercase text-text-tertiary">Wins</div>
        </div>
        <div className="w-10">
          <div className="text-sm font-bold text-text-secondary">
            {row.nominations}
          </div>
          <div className="text-[10px] uppercase text-text-tertiary">Noms</div>
        </div>
      </div>
    </button>
  )
}

function PersonRow({
  rank,
  row,
  onOpen,
}: {
  rank: number
  row: PersonAwardRow
  onOpen?: () => void
}) {
  const Content = (
    <>
      <span className="w-7 shrink-0 text-center text-sm font-black text-text-tertiary transition-colors group-hover:text-purple-400">
        #{rank}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-text-primary transition-colors group-hover:text-purple-300">
          {row.name}
        </p>
      </div>
      <div className="flex shrink-0 gap-3 text-right text-xs">
        <div className="w-10">
          <div className="text-sm font-bold text-accent-gold">{row.wins}</div>
          <div className="text-[10px] uppercase text-text-tertiary">Wins</div>
        </div>
        <div className="w-10">
          <div className="text-sm font-bold text-text-secondary">
            {row.nominations}
          </div>
          <div className="text-[10px] uppercase text-text-tertiary">Noms</div>
        </div>
      </div>
    </>
  )

  if (onOpen) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5"
      >
        {Content}
      </button>
    )
  }
  return (
    <div className="group flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left">
      {Content}
    </div>
  )
}
